import React, { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Link2, Check, Facebook, Linkedin, Instagram, MessageCircle, Twitter } from "lucide-react";

/**
 * Product share bar. Renders:
 *  - Copy Buy Link (uses clipboard API)
 *  - WhatsApp, Facebook, LinkedIn, Twitter/X, Instagram share buttons
 * All share URLs point to the direct product page so any recipient lands on Buy.
 */
export default function ShareButtons({ productId, productName, price }) {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/shop/${productId}`;
  const priceLine = price != null ? ` — ₹${Number(price).toFixed(2)}` : "";
  const textShort = `Check out *${productName}*${priceLine} on Immortal Vedas`;
  const textLong = `${textShort}\n\n${url}`;

  const copyLink = async () => {
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(url);
      } else {
        const ta = document.createElement("textarea");
        ta.value = url; document.body.appendChild(ta);
        ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
      }
      setCopied(true);
      toast.success("Buy link copied — paste anywhere to share");
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error("Could not copy the link");
    }
  };

  const openShare = (platform) => {
    const enc = encodeURIComponent;
    let target = "";
    switch (platform) {
      case "whatsapp":
        target = `https://wa.me/?text=${enc(textLong)}`;
        break;
      case "facebook":
        target = `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}&quote=${enc(textShort)}`;
        break;
      case "linkedin":
        target = `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`;
        break;
      case "twitter":
        target = `https://twitter.com/intent/tweet?text=${enc(textShort)}&url=${enc(url)}`;
        break;
      case "instagram":
        // Instagram has no public web-share endpoint — copy link and open IG.
        copyLink();
        toast.info("Paste the link in your Instagram story or DM.");
        target = "https://www.instagram.com/";
        break;
      default:
        return;
    }
    window.open(target, "_blank", "noopener,noreferrer");
  };

  const btn = "flex items-center justify-center w-10 h-10 rounded-full border border-border hover:bg-accent transition-all";

  return (
    <div className="mt-10 border-t border-border pt-6" data-testid="share-buttons">
      <div className="overline mb-4">Share this product</div>

      <div className="flex items-center gap-2 flex-wrap mb-4">
        <div className="flex-1 min-w-0 flex items-center border border-border rounded-full pl-4 pr-1 py-1 bg-card">
          {/* min-w-0 on both the row and the label: without it the unbreakable product
              URL sets a min-content width that stretches the whole page wider than a
              phone screen. */}
          <span className="text-xs text-muted-foreground truncate min-w-0 flex-1 mr-2" data-testid="share-link-text">{url}</span>
          <Button
            onClick={copyLink}
            size="sm"
            className={`btn-pill h-8 px-3 text-xs ${copied ? "bg-secondary text-secondary-foreground" : "bg-primary text-primary-foreground"}`}
            data-testid="share-copy-link"
          >
            {copied ? <><Check size={12} className="mr-1"/>Copied</> : <><Link2 size={12} className="mr-1"/>Copy Buy Link</>}
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={() => openShare("whatsapp")} className={btn} title="Share on WhatsApp" aria-label="Share on WhatsApp" data-testid="share-whatsapp">
          <MessageCircle size={16} className="text-[#25D366]" />
        </button>
        <button onClick={() => openShare("facebook")} className={btn} title="Share on Facebook" aria-label="Share on Facebook" data-testid="share-facebook">
          <Facebook size={16} className="text-[#1877F2]" />
        </button>
        <button onClick={() => openShare("linkedin")} className={btn} title="Share on LinkedIn" aria-label="Share on LinkedIn" data-testid="share-linkedin">
          <Linkedin size={16} className="text-[#0A66C2]" />
        </button>
        <button onClick={() => openShare("twitter")} className={btn} title="Share on X (Twitter)" aria-label="Share on X" data-testid="share-twitter">
          <Twitter size={16} className="text-foreground" />
        </button>
        <button onClick={() => openShare("instagram")} className={btn} title="Copy link for Instagram" aria-label="Copy link for Instagram" data-testid="share-instagram">
          <Instagram size={16} className="text-[#E1306C]" />
        </button>
        <span className="text-xs text-muted-foreground ml-2">Send to friends — one click and they land right on this product's Buy page.</span>
      </div>
    </div>
  );
}
